/**
 * GlobalUpgradeBar — sticky bottom-center floating "GO PRO" CTA visible on
 * every page for free + starter tier users. Self-hides for Pro users, and
 * also hides on auth/pricing routes where it'd be redundant.
 *
 * Dismissible per session (sessionStorage) — reappears on next visit so it
 * never gets permanently silenced.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Crown, X, Lightning } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialOffer } from '@/hooks/useTrialOffer';
import { useFootballPromotion } from '@/contexts/FootballPromotionContext';

const HIDE_PATHS = new Set([
  '/pricing',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/magic',
  '/payment-success',
]);

const DISMISS_KEY = 'pp_upgrade_bar_dismissed';

export default function GlobalUpgradeBar() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const { eligible: trialEligible, terms, starting, startTrial } = useTrialOffer();
  const { offer, loading: offerLoading } = useFootballPromotion();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch (_) { /* private mode */ }
  }, []);

  if (loading || offerLoading || ['free', 'paid', 'upcoming'].includes(offer?.state)) return null;
  if (dismissed) return null;
  if (HIDE_PATHS.has(pathname)) return null;
  // Pro users don't need the nudge
  if (user?.subscription === 'pro') return null;

  // Trial offer takes precedence for visitors + free users who are eligible.
  const isLoggedIn = !!user;
  const isStarter = user?.subscription === 'starter';
  const showTrial = trialEligible && !isStarter;

  let headline, subText, ctaLabel, onClick, linkTo;
  if (showTrial) {
    headline = `7 DAYS FREE — THEN $${terms.trial_total_after_trial}/3MO`;
    subText = `Try Pro free for ${terms.trial_days} days. Cancel anytime.`;
    ctaLabel = starting ? 'STARTING…' : isLoggedIn ? 'START FREE' : 'CLAIM IT';
    onClick = startTrial;
    linkTo = null;
  } else if (isStarter) {
    headline = 'GO PRO — $9.99/MO';
    subText = 'Unlimited AI picks, all 8 sports, ad-free.';
    ctaLabel = 'VIEW';
    linkTo = '/pricing';
  } else {
    // Free tier, trial already used: still show standard upgrade nudge
    headline = 'UPGRADE — $0.99/MO';
    subText = 'Unlock unlimited parlays + the full AI engine.';
    ctaLabel = 'VIEW';
    linkTo = '/pricing';
  }

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    setDismissed(true);
  };

  const CtaEl = linkTo ? Link : 'button';
  const ctaProps = linkTo
    ? { to: linkTo }
    : { type: 'button', onClick, disabled: starting };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none px-3 pb-3 sm:pb-5"
      data-testid="global-upgrade-bar"
    >
      <div
        className="pointer-events-auto relative flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 rounded-full bg-[#FF6B1A] text-black shadow-[0_8px_30px_rgba(255,107,26,0.45)] animate-pulse-glow w-full sm:w-auto max-w-[700px]"
      >
        <Crown size={20} weight="fill" className="shrink-0 text-black" />
        <div className="flex flex-col leading-tight min-w-0 flex-1 sm:flex-initial">
          <span className="font-heading font-black text-sm sm:text-base uppercase tracking-tight whitespace-nowrap">
            {headline}
          </span>
          <span className="text-[10px] sm:text-[11px] font-medium opacity-80 truncate">
            {subText}
          </span>
        </div>
        <CtaEl
          {...ctaProps}
          data-testid="global-upgrade-cta"
          className="shrink-0 inline-flex items-center gap-1 px-3 sm:px-4 py-1.5 sm:py-2 bg-black text-[#FF6B1A] font-heading font-black text-xs sm:text-sm uppercase tracking-wide rounded-full hover:bg-[#0f0f12] transition-colors disabled:opacity-60"
        >
          <Lightning size={14} weight="fill" />
          {ctaLabel}
        </CtaEl>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss upgrade bar"
          data-testid="global-upgrade-dismiss"
          className="shrink-0 p-1 -mr-1 hover:bg-black/10 rounded-full transition-colors"
        >
          <X size={14} weight="bold" className="text-black/70" />
        </button>
      </div>
    </div>
  );
}

